import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import { SELECT_OPTIONS } from '@components/common/form/constants/selectOptions';
import RadioButton from '@components/common/form/Radio';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import AccessCode from '@components/common/accesscode/AccessCode';
import Error from '@components/common/form/errorMessage';
import { ERROR_MESSAGES } from '@components/common/form/constants/validationErrorMessages';
import SSN from '@components/common/form/ssn/ssn';
import Tooltip from '@components/common/tooltip/Tooltip';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { IEX_STATE_CODES } from '@constants/configs/states';

interface IAccessCodeExpandedViewProps {
    values: {
        accessCodeSelection: string;
        accessCode: string;
        ssn: string;
    };
    i18nReplacements: {
        accessCode: React.ReactNode;
        ssn: React.ReactNode;
    };
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    handleFocus: (event: React.FocusEvent<HTMLInputElement>) => void;
    errors: {
        accessCode?: {
            type: string;
        };
        ssn?: {
            type: string;
        };
    };
    stateCode: string;
}

export default function AccessCodeExpandedView({
    values,
    i18nReplacements,
    handleChange,
    handleBlur,
    handleFocus,
    errors,
    stateCode,
}: IAccessCodeExpandedViewProps) {
    const ACCESS_CODE = 'accessCode';
    const SSN_CONSTANT = 'ssn';
    const REQUIRED = 'required';
    const EXACT_LENGTH = 'exactLength';
    return (
        <Fragment>
            <div className="grid-row gi-component gi-radio_button__nav-tabs">
                <fieldset className="usa-fieldset">
                    <legend className="usa-legend margin-b-10">
                        <span className="required-false">
                            <ConditionalRendering isTrue={stateCode !== IEX_STATE_CODES.PA}>
                                <FormattedMessage id="accounts.accessCode.make_selection" />
                            </ConditionalRendering>
                            <ConditionalRendering isTrue={stateCode === IEX_STATE_CODES.PA}>
                                <Tooltip
                                    {...{
                                        id: 'accounts.accessCode.make_selection',
                                        content: 'accounts.accessCode.make_selection.tooltip',
                                        dataAutomationId: 'access_code_expanded_view_account_accesscode_tooltip',
                                    }}
                                />
                            </ConditionalRendering>
                        </span>
                    </legend>

                    <ul className={'usa-list usa-list--unstyled gi-radio_button__tabs'}>
                        {SELECT_OPTIONS.ACCESSCODE_PAGE_PREFERENCE.map((option, i) => (
                            <RadioButton
                                key={i}
                                label={{
                                    labelClass: ['grid-row', 'gi-radio_button__label'],
                                    showRequiredIcon: false,
                                    labelText: option.text,
                                }}
                                inputData={{
                                    id: `accessCode_option_${i}`,
                                    isInputRequired: false,
                                    type: INPUT_TYPES.RADIO_BUTTON,
                                    name: 'accessCodeSelection',
                                    liClass: [''],
                                    value: option.value,
                                    checked: option.value === values.accessCodeSelection,
                                    ariaChecked: option.value,
                                    dataValue: option.value,
                                    ariaLabelledby: `accessCode_option_${i}_label${i}`,
                                }}
                                errorClasses={['margin-l-0 margin-b-10']}
                                actions={{
                                    handleFieldChange: handleChange,
                                    handleFieldFocus:handleFocus
                                }}
                            />
                        ))}
                    </ul>
                </fieldset>
            </div>
            <div className="desktop:grid-col-9 tablet:grid-col-9 mobile:grid-col-12 grid-col-12 gi-radio_button__tabs__content">
                <ConditionalRendering isTrue={ACCESS_CODE === values.accessCodeSelection}>
                    <Fragment>
                        <AccessCode
                            labelText="accounts.login_success.enter_access_code"
                            labelClasses={['padding-l-0']}
                            isRequired={false}
                            wrapperClasses={['grid-row gi-accesscode margin-t-0']}
                            inputClasses={['grid-col-12']}
                            value={values.accessCode}
                            isInputRequired={false}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onFocus={handleFocus}
                            maxLength={10}
                            messageText={i18nReplacements.accessCode}
                            errorWrapperClasses={['access-code__error__wrapper']}
                            errorClasses={['margin-t-10']}
                        />
                        <Error
                            isVisible={errors.accessCode && errors.accessCode.type === REQUIRED}
                            messageText={ERROR_MESSAGES.REQUIRED}
                            textReplacements={i18nReplacements.accessCode}
                            errorWrapperClasses={['access-code__error__wrapper']}
                            errorClasses={['center']}
                        />
                        <Error
                            isVisible={errors.accessCode && errors.accessCode.type === EXACT_LENGTH}
                            messageText={ERROR_MESSAGES.NOT_VALID}
                            textReplacements={i18nReplacements.accessCode}
                            errorWrapperClasses={['grid-row', 'margin-tb-5 ', 'access-code__error__wrapper']}
                            errorClasses={['center']}
                        />
                    </Fragment>
                </ConditionalRendering>
                <ConditionalRendering isTrue={SSN_CONSTANT === values.accessCodeSelection}>
                    <Fragment>
                        <SSN
                            inputData={{
                                id: 'ssn',
                                name: 'ssn',
                                type: INPUT_TYPES.TEXT,
                                value: values.ssn,
                                inputClasses: ['grid-col-12'],
                                isRequired: false,
                                isInputRequired: true,
                                wrapperClasses: [],
                                errorWrapperClasses: [],
                            }}
                            label={{
                                labelClasses: ['padding-l-0'],
                                labelText: 'accounts.login_success.enter_ssn',
                            }}
                            legend={{
                                legendText: 'accounts.login_success.dont_have_access_code',
                                helperClasses: ['margin-t-0'],
                            }}
                            error={{
                                errorClasses: [],
                            }}
                            actions={{
                                handleFieldChange: handleChange,
                            }}
                        />
                        <Error
                            isVisible={errors.ssn && errors.ssn.type === EXACT_LENGTH}
                            messageText={ERROR_MESSAGES.REQUIRED}
                            textReplacements={i18nReplacements.accessCode}
                            errorWrapperClasses={['grid-row', 'margin-tb-5 ', 'access-code__error__wrapper']}
                            errorClasses={['center']}
                        />
                    </Fragment>
                </ConditionalRendering>
            </div>
        </Fragment>
    );
}
